import { Loader2, CheckCircle2, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { INPUT_CLS } from './constants';
import RecentQuotesPicker from '../RecentQuotesPicker';

export const StepAccountQuote = ({
  step, mode, showAccessRequest,
  fastTrack, localQuoteForm,
  pdfDragOver, setPdfDragOver,
  handleFastTrackDrop, handleFastTrackBrowse,
  fastTrackInputRef, pdfParsing,
  accountQuery, setAccountQuery,
  accountLoading, accounts,
  selectedAccount, setSelectedAccount,
  showProspectCreate, setShowProspectCreate,
  prospectAccountName, setProspectAccountName,
  prospectAccountPhone, setProspectAccountPhone,
  prospectContactFirst, setProspectContactFirst,
  prospectContactLast, setProspectContactLast,
  prospectContactEmail, setProspectContactEmail,
  prospectContactMobile, setProspectContactMobile,
  creatingProspect, createProspect,
  runAccountSearch, quoteForm, setStep,
  onSelectRecentQuote, onHistoryClick,
}) => {
  if (step !== 1 || mode !== 'quote' || showAccessRequest) return null;
  return (
    <>
      {fastTrack && !localQuoteForm && (
        <div className="space-y-3">
          {/* ── Recent quotes picker — click to skip PDF upload ── */}
          <RecentQuotesPicker onSelect={onSelectRecentQuote} onHistoryClick={onHistoryClick} />

          {/* Divider */}
          <div className="relative flex items-center gap-2">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-[11px] text-gray-400 font-medium">or upload a new PDF</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          <div className="text-sm text-gray-700">
            Drop the quote PDF below. We&apos;ll parse the customer name and items silently — no quote will be created in C30.
          </div>
          <div
            onDragOver={(e) => { e.preventDefault(); setPdfDragOver(true); }}
            onDragLeave={() => setPdfDragOver(false)}
            onDrop={handleFastTrackDrop}
            className={`rounded-2xl border-2 border-dashed p-8 text-center transition-colors ${pdfDragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'}`}
            data-testid="crm-fasttrack-dropzone"
          >
            <input ref={fastTrackInputRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={handleFastTrackBrowse} />
            {pdfParsing ? (
              <div className="flex flex-col items-center gap-2 text-sm text-gray-700">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                Parsing PDF…
              </div>
            ) : (
              <>
                <div className="font-semibold text-gray-900">Drag &amp; drop a quote PDF</div>
                <div className="text-xs text-gray-500 mt-1">or</div>
                <Button onClick={() => fastTrackInputRef.current?.click()} className="mt-2 bg-blue-600 hover:bg-blue-700 text-white">Browse files</Button>
              </>
            )}
          </div>
          <div className="flex justify-between gap-2 pt-2">
            <Button variant="outline" onClick={() => setStep(0)}>← Back</Button>
          </div>
        </div>
      )}

      {(!fastTrack || localQuoteForm) && !showProspectCreate && (
        <>
          <Label className="text-sm font-semibold">Search customer in Zoho</Label>
          <div className="flex gap-2">
            <Input
              value={accountQuery}
              onChange={(e) => setAccountQuery(e.target.value)}
              placeholder="Customer name (e.g. A W Lumb)"
              className={`flex-1 ${INPUT_CLS}`}
              data-testid="crm-account-search"
            />
            <Button type="button" onClick={() => runAccountSearch(accountQuery)} disabled={accountLoading}>
              {accountLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>
          <p className="text-xs text-gray-500 -mt-1">
            Click a customer below from the list,{' '}
            <button
              type="button"
              onClick={() => { setShowProspectCreate(true); setProspectAccountName(accountQuery || quoteForm?.customer_name || ''); }}
              className="text-blue-700 font-semibold underline hover:text-blue-800"
              data-testid="crm-add-prospect-btn"
            >
              or add a new prospect customer
            </button>.
          </p>
          <div className="border-2 border-gray-200 rounded-xl bg-white divide-y max-h-80 overflow-y-auto">
            {accountLoading ? (
              <div className="p-4 text-center text-gray-500 text-sm">Searching Zoho…</div>
            ) : accounts.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">No accounts found</div>
            ) : (
              accounts.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => setSelectedAccount(a)}
                  className={`w-full text-left p-3 hover:bg-gray-50 flex items-center justify-between transition-colors ${selectedAccount?.id === a.id ? 'bg-emerald-50 border-l-4 border-emerald-600' : ''}`}
                  data-testid={`crm-account-option-${a.id}`}
                >
                  <div>
                    <div className="font-semibold text-sm text-gray-900 flex items-center gap-2">
                      {a.name}
                      {a.matched_by_code && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700" data-testid="crm-account-matched-by-code-badge">
                          Matched by account code
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">
                      {[a.city, a.country, a.customer_code ? `#${a.customer_code}` : null].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  {selectedAccount?.id === a.id && <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
                </button>
              ))
            )}
          </div>
        </>
      )}

      {showProspectCreate && (
        <div className="border-2 border-blue-200 rounded-xl p-4 bg-blue-50/30 space-y-3" data-testid="crm-prospect-form">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-sm text-blue-900">New Prospect Customer</div>
            <button type="button" onClick={() => setShowProspectCreate(false)} className="text-xs text-gray-500 hover:text-gray-700 underline" data-testid="crm-prospect-cancel">
              Back to search
            </button>
          </div>
          <div className="text-[11px] text-gray-500">
            Creates a new <b>Account</b> (Type = Prospect) and a linked primary <b>Contact</b> in Zoho. Required fields are marked with *.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Account Name *</Label>
              <Input value={prospectAccountName} onChange={(e) => setProspectAccountName(e.target.value)} placeholder="e.g. Smith Roofing Ltd" className={INPUT_CLS} data-testid="crm-prospect-account-name" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Account Phone *</Label>
              <Input value={prospectAccountPhone} onChange={(e) => setProspectAccountPhone(e.target.value)} placeholder="e.g. 0121 555 0123" className={INPUT_CLS} data-testid="crm-prospect-account-phone" />
            </div>
            <div>
              <Label className="text-xs">Contact First Name</Label>
              <Input value={prospectContactFirst} onChange={(e) => setProspectContactFirst(e.target.value)} className={INPUT_CLS} data-testid="crm-prospect-contact-first" />
            </div>
            <div>
              <Label className="text-xs">Contact Last Name *</Label>
              <Input value={prospectContactLast} onChange={(e) => setProspectContactLast(e.target.value)} className={INPUT_CLS} data-testid="crm-prospect-contact-last" />
            </div>
            <div>
              <Label className="text-xs">Contact Email</Label>
              <Input type="email" value={prospectContactEmail} onChange={(e) => setProspectContactEmail(e.target.value)} placeholder="name@company.com" className={INPUT_CLS} data-testid="crm-prospect-contact-email" />
            </div>
            <div>
              <Label className="text-xs">Contact Mobile</Label>
              <Input value={prospectContactMobile} onChange={(e) => setProspectContactMobile(e.target.value)} placeholder="07…" className={INPUT_CLS} data-testid="crm-prospect-contact-mobile" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowProspectCreate(false)}>Cancel</Button>
            <Button
              size="sm"
              onClick={createProspect}
              disabled={creatingProspect || !prospectAccountName.trim() || !prospectAccountPhone.trim() || !prospectContactLast.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="crm-prospect-submit"
            >
              {creatingProspect ? (<><Loader2 className="w-4 h-4 animate-spin mr-1" />Creating…</>) : 'Create Prospect'}
            </Button>
          </div>
        </div>
      )}

      {(!fastTrack || localQuoteForm) && (
        <div className="flex justify-between gap-2 pt-2">
          <Button variant="outline" onClick={() => setStep(0)}>← Back</Button>
          <Button onClick={() => setStep(2)} disabled={!selectedAccount || showProspectCreate} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="crm-next-btn">
            Next: Quote Details
          </Button>
        </div>
      )}
    </>
  );
};
